// frontend/src/pages/SalesDetail.jsx (revised validation of invoice figures)
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { fmtDate } from '../utils/time';

const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const money = (n) => `₹${toNum(n).toFixed(2)}`;
function round2(n){ return Number((Math.round(n * 100) / 100).toFixed(2)); }

/* ---------------------- Small UI helpers ---------------------- */
function Alert({ message, onClose }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        marginTop: 12,
        padding: '10px 12px',
        borderRadius: 8,
        border: '1px solid #fecaca',
        background: '#fff1f2',
        color: '#b91c1c',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
        <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>⚠ {message}</div>
        <button className="btn btn-sm secondary" onClick={onClose} aria-label="Dismiss">Dismiss</button>
      </div>
    </div>
  );
}

/* --------- Open API PDF safely; map backend errors to UX text --------- */
async function openPdfSafe(apiPath, setUiError) {
  try {
    const resp = await api.get(apiPath, { responseType: 'blob' });
    const blob =
      resp?.data instanceof Blob
        ? resp.data
        : new Blob([resp?.data ?? ''], { type: resp?.headers?.['content-type'] || 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    let data = err?.response?.data;
    try {
      if (data instanceof Blob) {
        const txt = await data.text();
        try { data = JSON.parse(txt); } catch { data = { error: txt }; }
      }
    } catch {}
    const error = data?.error || 'Unable to open document.';
    const missing = Array.isArray(data?.missing) ? data.missing[0] : undefined;

    let msg = 'Unable to open document.';
    if (error === 'Forbidden' && missing) {
      if (missing === 'invoices:export') msg = 'You don’t have permission to view or export invoices.';
      else if (missing === 'credit:export') msg = 'You don’t have permission to view or export credit notes.';
      else msg = 'You don’t have permission to view this document.';
    } else if (typeof error === 'string' && error.toLowerCase().includes('no returns')) {
      msg = 'No return entries were found for this invoice.';
    } else if (typeof error === 'string') {
      msg = error;
    }
    setUiError(msg);
  }
}

// --- Soft derivations for per-line discount fields ---
function deriveLineFields(it) {
  const qty = toNum(it.qty, 0);
  const price = toNum(it.price, 0);              // unit price BEFORE discount
  const gstRate = toNum(it.gst_rate ?? it.gstRate, 0);

  const pct = toNum(it.discount_pct, NaN);
  const net = toNum(it.net_price, NaN);
  const discPerItem = toNum(it.discount_per_item, NaN);

  let discountPct, netUnit, perItemDisc;

  if (!Number.isNaN(pct)) {
    discountPct = Math.max(0, Math.min(100, pct));
    perItemDisc = round2(price * discountPct / 100);
    netUnit = round2(price - perItemDisc);
  } else if (!Number.isNaN(net)) {
    netUnit = round2(net);
    perItemDisc = round2(price - netUnit);
    discountPct = price > 0 ? round2((perItemDisc / price) * 100) : 0;
  } else if (!Number.isNaN(discPerItem)) {
    perItemDisc = round2(discPerItem);
    netUnit = round2(price - perItemDisc);
    discountPct = price > 0 ? round2((perItemDisc / price) * 100) : 0;
  } else {
    // no discount fields at all
    discountPct = 0;
    perItemDisc = 0;
    netUnit = price;
  }

  // totals computed: discounted base → GST
  const lineExcl = round2(qty * netUnit);
  const lineGst  = round2(lineExcl * gstRate / 100);
  const lineIncl = round2(lineExcl + lineGst);

  return {
    qty, price, gstRate,
    discountPct, perItemDisc, netUnit,
    totalExcl: lineExcl,
    gstAmt: lineGst,
    totalIncl: lineIncl,
  };
}

export default function SalesDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // UX error for PDF attempts
  const [uiError, setUiError] = useState('');

  // aggregated credit notes (refunds) for this invoice
  const [creditRefunded, setCreditRefunded] = useState(0);
  const [refundedTax, setRefundedTax] = useState({ cgst: 0, sgst: 0, igst: 0, hasTaxBreakup: false });

  // UX: copy invoice no
  const [copied, setCopied] = useState(false);
  const copyInvoiceNumber = async () => {
    const text = String(invoice?.invoice_no || id || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1200); }
      finally { document.body.removeChild(ta); }
    }
  };

  // 1) Load invoice
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/sales/${encodeURIComponent(id)}`);
        if (cancelled) return;
        const data = r.data || {};
        if (typeof data.items === 'string') {
          try { data.items = JSON.parse(data.items); } catch {}
        }
        setInvoice(data);
        setErr('');
      } catch (e) {
        setErr(e?.response?.data?.error || 'Failed to load invoice');
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // 2) Load all credit notes for this invoice (for *after return* blocks)
  useEffect(() => {
    if (!invoice?.invoice_no) return;
    let cancelled = false;
    (async () => {
      try {
        const invNo = String(invoice.invoice_no).trim();
        const resp = await api.get(`/credit-notes?search=${encodeURIComponent(invNo)}&limit=all`);
        const list = Array.isArray(resp.data) ? resp.data : [];
        const refundedAmount = list.reduce((s, x) => s + Math.abs(toNum(x.total ?? x.amount)), 0);
        const cgst = list.reduce((s, x) => s + Math.abs(toNum(x.cgst)), 0);
        const sgst = list.reduce((s, x) => s + Math.abs(toNum(x.sgst)), 0);
        const igst = list.reduce((s, x) => s + Math.abs(toNum(x.igst)), 0);
        const hasTaxBreakup = list.some(x =>
          Number.isFinite(Number(x?.cgst)) || Number.isFinite(Number(x?.sgst)) || Number.isFinite(Number(x?.igst))
        );
        if (!cancelled) {
          setCreditRefunded(refundedAmount);
          setRefundedTax({ cgst, sgst, igst, hasTaxBreakup });
        }
      } catch {
        if (!cancelled) {
          setCreditRefunded(0);
          setRefundedTax({ cgst: 0, sgst: 0, igst: 0, hasTaxBreakup: false });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [invoice?.invoice_no]);

  const items = useMemo(() => (Array.isArray(invoice?.items) ? invoice.items : []), [invoice]);

  const rows = useMemo(
    () => items.map((it, idx) => ({ _idx: idx, name: it.name || it.product_name || it.title, ...deriveLineFields(it) })),
    [items]
  );

  // ── Trust DB but validate; if inconsistent with items, recompute
  const dbSubtotalRaw  = toNum(invoice?.subtotal);
  const dbDiscountRaw  = toNum(invoice?.discount);
  const dbCgstRaw      = toNum(invoice?.cgst);
  const dbSgstRaw      = toNum(invoice?.sgst);
  const dbIgstRaw      = toNum(invoice?.igst);
  const dbTaxTotalRaw  = round2(dbCgstRaw + dbSgstRaw + dbIgstRaw);
  const dbGrandRaw     = toNum(invoice?.total);

  // Compute from items (discounted base then GST)
  const calcSubtotal = round2(rows.reduce((s, r) => s + r.qty * toNum(invoice?.items?.[r._idx]?.price ?? r.price), 0));
  const calcDiscount = round2(rows.reduce((s, r) => s + r.qty * r.perItemDisc, 0));
  const calcGstTotal = round2(rows.reduce((s, r) => s + r.gstAmt, 0));
  const calcGrand    = round2(calcSubtotal - calcDiscount + calcGstTotal);

  // Accept DB unless it is clearly off (difference > 0.5)
  const pick = (db, calc) => (Math.abs(toNum(db) - calc) > 0.5 ? calc : toNum(db));

  const subtotal = pick(dbSubtotalRaw, calcSubtotal);
  const discount = pick(dbDiscountRaw, calcDiscount);

  // Validate tax split: ensure cgst+sgst+igst equals gstTotal; else override
  let cgst = toNum(dbCgstRaw), sgst = toNum(dbSgstRaw), igst = toNum(dbIgstRaw);
  let taxTotal = round2(cgst + sgst + igst);
  if (Math.abs(taxTotal - calcGstTotal) > 0.5) {
    // Heuristic: if DB had some IGST, assume inter; else assume intra split
    if (igst > 0.01) {
      igst = calcGstTotal; cgst = 0; sgst = 0;
    } else {
      const half = round2(calcGstTotal / 2);
      // keep cents sum exact
      const other = round2(calcGstTotal - half);
      cgst = half; sgst = other; igst = 0;
    }
    taxTotal = round2(cgst + sgst + igst);
  }

  const grand = pick(dbGrandRaw, round2(subtotal - discount + taxTotal));

  // AFTER RETURN values
  const ratio = grand > 0 ? Math.min(1, creditRefunded / grand) : 0;
  const cgstAfterReturn = refundedTax.hasTaxBreakup ? Math.max(0, round2(cgst - refundedTax.cgst)) : Math.max(0, round2(cgst * (1 - ratio)));
  const sgstAfterReturn = refundedTax.hasTaxBreakup ? Math.max(0, round2(sgst - refundedTax.sgst)) : Math.max(0, round2(sgst * (1 - ratio)));
  const igstAfterReturn = refundedTax.hasTaxBreakup ? Math.max(0, round2(igst - refundedTax.igst)) : Math.max(0, round2(igst * (1 - ratio)));
  const saleValueAfterCreditRefund = Math.max(0, round2(grand - creditRefunded));

  // Actions
  const handlePrint = () => window.print();
  const openInvoicePdf = () =>
    openPdfSafe(`/sales/${encodeURIComponent(id)}/pdf`, setUiError);
  const openCreditPdf  = () => {
    if (!invoice?.invoice_no) return;
    openPdfSafe(`/credit-notes/by-invoice/${encodeURIComponent(invoice.invoice_no)}/pdf`, setUiError);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card" style={{ marginTop: 12, padding: 16 }}>
          <div className="mono">Loading invoice…</div>
          <div className="skeleton" style={{ height: 12, width: '60%', marginTop: 8 }} />
          <div className="skeleton" style={{ height: 12, width: '40%', marginTop: 8 }} />
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="container">
        <div className="card" style={{ marginTop: 12, padding: 16, border: '1px solid #fecaca', background: '#fff1f2' }}>
          <div className="mono" style={{ color: '#b91c1c' }}>⚠ {err}</div>
        </div>
        <div style={{ marginTop: 8 }}><button className="btn" onClick={() => navigate(-1)}>← Back</button></div>
      </div>
    );
  }
  if (!invoice) return null;

  // —— small pill component to keep the "look"
  const Chip = ({ children, tone = 'neutral' }) => {
    const tones = {
      neutral: { bg: '#f1f5f9', bd: '#e2e8f0', fg: '#0f172a' },
      info:    { bg: '#eef6ff', bd: '#bcd7ff', fg: '#0b5cab' },
      good:    { bg: '#ecfdf5', bd: '#bbf7d0', fg: '#065f46' },
    }[tone];
    return (
      <span className="mono" style={{ background: tones.bg, border: `1px solid ${tones.bd}`, color: tones.fg, padding: '4px 8px', borderRadius: 999 }}>
        {children}
      </span>
    );
  };

  const tinyBtn = {
    marginLeft: 8, fontSize: 12, padding: '2px 8px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer',
  };

  return (
    <div className="container" style={{ paddingBottom: 24 }}>
      {/* Friendly error banner for PDF attempts */}
      <Alert message={uiError} onClose={() => setUiError('')} />

      {/* Header / actions */}
      <div className="card" style={{ marginTop: 8, padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => navigate(-1)}>← Back</button>
          <h2 style={{ margin: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>Invoice</span>
            <span className="mono" onClick={copyInvoiceNumber} title="Click to copy invoice number" style={{ cursor: 'pointer', userSelect: 'none' }}>
              - {invoice.invoice_no || id}
            </span>
            <button type="button" aria-label="Copy invoice number" title="Copy invoice number" onClick={copyInvoiceNumber} style={tinyBtn}>📋 Copy</button>
            {copied && <span className="mono" style={{ marginLeft: 6, fontSize: 12, color: '#065f46' }}>✓ Copied!</span>}
          </h2>
          <div className="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button className="btn" onClick={handlePrint}>🖨 Print</button>
            <button className="btn" onClick={openInvoicePdf}>📄 Invoice PDF</button>
            <button className="btn" onClick={openCreditPdf}>📄 Credit Note</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
          <Chip tone="info">Date: {fmtDate(invoice.sold_at || invoice.created_at)}</Chip>
          <Chip tone="neutral">Customer: {invoice.customer_name || invoice.customer?.name || '-'}</Chip>
          <Chip tone={creditRefunded > 0 ? 'good' : 'neutral'}>Credit refunded: {money(creditRefunded)}</Chip>
          <Chip tone="neutral">Payment: {invoice.payment_method || '-'}</Chip>
        </div>
      </div>

      {/* Items */}
      <div className="card" style={{ marginTop: 12, padding: 12, overflowX: 'auto' }}>
        <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Items</h3>
          <div className="mono" style={{ opacity: 0.7 }}>{rows.length} item{rows.length === 1 ? '' : 's'}</div>
        </div>
        <table className="table" style={{ minWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ width: 60, textAlign: 'right' }}>#</th>
              <th>Name</th>
              <th style={{ width: 90, textAlign: 'right' }}>Qty</th>
              <th style={{ width: 120, textAlign: 'right' }}>Price</th>
              <th style={{ width: 90, textAlign: 'right' }}>Disc %</th>
              <th style={{ width: 110, textAlign: 'right' }}>Disc / item</th>
              <th style={{ width: 110, textAlign: 'right' }}>Net unit</th>
              <th style={{ width: 90, textAlign: 'right' }}>GST %</th>
              <th style={{ width: 150, textAlign: 'right' }}>Total (incl. GST)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._idx}>
                <td className="mono" style={{ textAlign: 'right' }}>{r._idx + 1}</td>
                <td>{r.name}</td>
                <td className="mono" style={{ textAlign: 'right' }}>{r.qty}</td>
                <td className="mono" style={{ textAlign: 'right' }}>{money(r.price)}</td>
                <td className="mono" style={{ textAlign: 'right' }}>{toNum(r.discountPct).toFixed(2)}</td>
                <td className="mono" style={{ textAlign: 'right' }}>{money(r.perItemDisc)}</td>
                <td className="mono" style={{ textAlign: 'right' }}>{money(r.netUnit)}</td>
                <td className="mono" style={{ textAlign: 'right' }}>{toNum(r.gstRate).toFixed(2)}</td>
                <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>{money(r.totalIncl)}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={9} style={{ textAlign: 'center', padding: 16 }}>No items</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="card" style={{ marginTop: 12, padding: 12 }}>
        <h3 style={{ marginTop: 0 }}>Summary</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          {/* 1) ACTUAL INVOICE VALUES */}
          <SummaryRow label="Subtotal (pre-GST)" value={money(subtotal)} />
          <SummaryRow label="Discount" value={money(discount)} />
          <SummaryRow label="CGST – Invoice" value={money(cgst)} />
          <SummaryRow label="SGST – Invoice" value={money(sgst)} />
          <SummaryRow label="IGST – Invoice" value={money(igst)} />
          <SummaryRow label="Tax Total invoice" value={money(taxTotal)} strong />
          <SummaryRow label="Grand Total Invoice" value={money(grand)} strong />

          {/* 2) AFTER RETURNS */}
          <SummaryRow label="Credit refunded" value={money(creditRefunded)} />
          <SummaryRow label="CGST – Invoice After Return" value={money(cgstAfterReturn)} />
          <SummaryRow label="SGST – Invoice After Return" value={money(sgstAfterReturn)} />
          <SummaryRow label="IGST – Invoice After Return" value={money(igstAfterReturn)} />

          {/* 3) Sale value after credit */}
          <SummaryRow label="Sale value After Credit Refund" value={money(saleValueAfterCreditRefund)} strong accent={saleValueAfterCreditRefund > 0 ? 'warn' : 'good'} />
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, strong = false, accent = 'neutral' }) {
  const tones = {
    neutral: { bg: '#f8fafc', fg: '#0f172a', bd: '#e2e8f0' },
    good:    { bg: '#ecfdf5', fg: '#065f46', bd: '#bbf7d0' },
    warn:    { bg: '#fff7ed', fg: '#9a3412', bd: '#fed7aa' },
  }[accent];
  return (
    <div
      className="mono"
      style={{
        background: tones.bg,
        border: `1px solid ${tones.bd}`,
        color: tones.fg,
        padding: '10px 12px',
        borderRadius: 8,
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <span style={{ opacity: 0.75 }}>{label}</span>
      <span style={{ fontWeight: strong ? 700 : 600 }}>{value}</span>
    </div>
  );
}
